import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { UseCases } from "@planzone/core";
import {
  useListRetrieval,
  useListEdition,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { Assignable } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { Loader } from "@common/loader";
import { TagList } from "@common/tagList";
import { Safeguard } from "@scenes/safeguard";
import { ElementAttachments } from "@common/elementAttachments";
import { Comments } from "@scenes/comments";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { ScenePlaceholder } from "@common/scenePlaceholder";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { ProjectElementsSecondaryCriteria } from "@scenes/projectElementsSecondaryCriteria";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Breadcrumbs } from "@common/breadcrumbs";
import { FolderIcon } from "@common/folderIcon";
import { ZoneAvatar } from "@common/zoneAvatar";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { Props } from "./types";
import { Details } from "./details";
import { Timestamp } from "./timestamp";
import styles from "./styles.scss";

function ListPage({ listId }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    isRequestPending,
    isRequestFailure,
    list,
    hasRetrievedList,
    retrieveList,
  } = useListRetrieval(listId);
  const { pushView } = useModal();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    list.project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const {
    isRequestPending: isEditRequestPending,
    editList,
    error,
  } = useListEdition();
  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  React.useEffect(() => {
    retrieveList(listId);
  }, [listId]);

  return (
    <WorkspaceContext type="project" project={list.project}>
      <Safeguard
        useCases={[UseCases.editList, UseCases.postComment]}
        context={{ list }}
      >
        {({ canEditList, canPostComment }) => (
          <div
            className={classNames(
              { [styles.list]: !isRequestPending },
              { [styles.listLoading]: isRequestPending },
            )}
          >
            {isRequestPending ? (
              <div className={styles.loader}>
                <div className={styles.loaderMain}>
                  <Loader />
                </div>
              </div>
            ) : isRequestFailure ? (
              <div className={styles.scenePlaceholder}>
                <ScenePlaceholder goBack={goBack} />
              </div>
            ) : (
              <div className={styles.contentModal}>
                <div className={styles.header}>
                  <div className={styles.contentHeader}>
                    <h1 className={styles.title}>
                      <Breadcrumbs
                        breadcrumbs={[
                          {
                            name: list.zone.name,
                            to: `/zones/${list.zone.uuid}`,
                            icon: (
                              <ZoneAvatar
                                name={list.zone.name}
                                color={list.zone.color}
                              />
                            ),
                          },
                          {
                            name: list.project.name,
                            to: `/projects/${list.project.uuid}`,
                            icon: <FolderIcon className={styles.folderIcon} />,
                          },
                          { name: list.name },
                        ]}
                      />
                    </h1>

                    <div className={styles.rightContent}>
                      <div className={styles.profileNavigation}>
                        <AssistantShortcut />
                        <Notifications />
                        <ProfileNavigation />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.main}>
                  <div className={styles.content}>
                    <div className={styles.scrollWrapper}>
                      <div className={styles.details}>
                        <Details
                          list={list}
                          error={error}
                          readOnly={!canEditList}
                        />
                      </div>
                      <div className={styles.tagList}>
                        <TagList
                          id={list.uuid}
                          tags={list.tags}
                          type={Assignable.LIST}
                          project={list.project}
                          readOnly={!canEditList}
                        />
                      </div>
                      <Safeguard
                        useCases={[
                          UseCases.editProjectElementDetailsConfiguration,
                        ]}
                        context={{ list }}
                      >
                        {({ canEditProjectElementDetailsConfiguration }) =>
                          (template.length > 0 ||
                            canEditProjectElementDetailsConfiguration) && (
                            <div className={styles.secondaryCriteria}>
                              <div className={styles.row}>
                                <p className={styles.label}>
                                  {t("listRetrieval.secondaryCriteria")}
                                </p>

                                {canEditProjectElementDetailsConfiguration ? (
                                  <div
                                    className={styles.configurationIconBox}
                                    onClick={() =>
                                      pushView({
                                        key: UseCases.retrieveProjectElementDetailsConfiguration,
                                        data: { project: list.project },
                                      })
                                    }
                                  >
                                    <CogWheelIcon
                                      className={styles.configurationIcon}
                                    />
                                  </div>
                                ) : (
                                  <div className={styles.contextualMenuIcon} />
                                )}
                              </div>
                              <ProjectElementsSecondaryCriteria
                                project={list.project}
                                criteriaForms={template.reduce(
                                  (acc, criteria) => ({
                                    ...acc,
                                    [criteria.uuid]: (
                                      <SecondaryCriteriaForm
                                        criteria={criteria}
                                        value={
                                          list.attributes
                                            ? list.attributes[criteria.uuid] ||
                                              ""
                                            : ""
                                        }
                                        flexible
                                        disabled={isEditRequestPending}
                                        submit={(value) =>
                                          editList({
                                            list,
                                            nextList: {
                                              ...list,
                                              attributes: {
                                                ...list.attributes,
                                                [criteria.uuid]: value,
                                              },
                                            },
                                          })
                                        }
                                      />
                                    ),
                                  }),
                                  {},
                                )}
                              />
                            </div>
                          )
                        }
                      </Safeguard>
                      <ElementAttachments
                        element={list}
                        project={list.project}
                        elementType="LIST"
                        readOnly={!canEditList}
                      />
                    </div>
                  </div>
                  <div className={styles.timeline}>
                    <div className={styles.timestamp}>
                      <Timestamp
                        list={list}
                        error={error}
                        readOnly={!canEditList}
                      />
                    </div>
                    <div className={styles.comments}>
                      {!list.project.isTemplate && (
                        <Comments
                          subject={list.uuid}
                          type="list"
                          canPost={canPostComment}
                          project={list.project.uuid}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Safeguard>
    </WorkspaceContext>
  );
}

export { ListPage };
